import global from "@dojo/framework/shim/global";
import { WidgetMetaProperties } from "@dojo/framework/core/interfaces";
import MetaBase from "@dojo/framework/core/meta/Base";
import { BottomRight, TopLeft } from "@dojo/framework/core/meta/Dimensions";
import { IConnectionShape, IGroupShape, IShape } from "../interfaces/model/PageObjects/index";
import { getZoomValue } from "../selectors/index";
import { getDistance, getMappedCurvePoint } from "../util";
import { IPosition } from "./Drag/CsiDragController";
import { ICustomShapePoint } from "../interfaces/model/dataStructures/CustomShapePoint";

/**
 * Meta to get closest point on curve of given point
 */
class CurveTransientPoint extends MetaBase {
	private clientPosition?: IPosition;

	constructor(properties: WidgetMetaProperties) {
		super(properties);
		const win: Window = global.window;
		win.addEventListener("pointermove", this._onPointerMove.bind(this), true);
	}

	/**
   * Pointer move event on window
   * @param event {PointerEvent}
   */
	public _onPointerMove(event: PointerEvent) {
		this.clientPosition = { x: event.clientX, y: event.clientY };
		this.invalidate();
	}

	/**
   * Get closest point to a given one on a given path.
   * @param key {string} key to node
   * @param pageObject {IShape | IConnectionShape}
   * @param parentSize {TopLeft & BottomRight}
   * @return {Array | undefined}
   */
	public get(
		key: string, pageObject: IShape | IConnectionShape, parentSize: TopLeft & BottomRight,
		groupObject?: IGroupShape, shapePoints? : ICustomShapePoint[],
	) {
		const node = this.getNode(key) as HTMLElement;
		if (this.clientPosition) {
			const parameters = {
				groupObject,
				position: this.clientPosition,
				pageObject,
				parentSize,
				shapePoints,
			};
			return this.closestPoint(node, parameters);
		}
		return undefined;
	}

	/**
   * Close point function returns the closest point for curve from given point
   * Note:- Algorithm is copied from http://bl.ocks.org/mbostock/8027637
   * @param pathNode {any}  Svg path node
   * @param inputDetails {any} bundled input values
   * @return {array[]}
   */
	protected closestPoint(pathNode: any, inputDetails: any) {
		const { groupObject, position, pageObject, parentSize, shapePoints } = inputDetails;
		// point index; keep track of which point we have reached
		let [pi, prevLength] = [0, 0];
		if (pathNode) {
			const mappedLocation = getMappedCurvePoint(pageObject, position, parentSize, groupObject);
			const point: number[] = [];

			point[0] = mappedLocation.x;
			point[1] = mappedLocation.y;

			const pathLength = pathNode.getTotalLength();
			let precision: number = 8;
			let best: IPosition = { x: 0, y: 0 };
			let [bestPi, bestPrevLength, bestNextLength, bestLength] = [0, 0, 0, 0];

			let bestDistance = Infinity;

			// linear scan for coarse approximation
			for (let scanLength = 0; scanLength <= pathLength; scanLength += precision) {
				const scan = pathNode.getPointAtLength(scanLength);
				const scanDistance = distance2(scan, point);
				if (shapePoints && shapePoints[pi+1] && getDistance(shapePoints[pi+1].position, scan) <= precision / 2 ) {
					if (bestPi == pi) {
						bestNextLength = scanLength;
					}
					pi++;
					prevLength = scanLength;
				}
				if (scanDistance < bestDistance) {
					best = scan;
					bestLength = scanLength;
					bestDistance = scanDistance;
					bestPi = pi;
					bestPrevLength = prevLength;
				}
			}

			// binary search for precise estimate
			precision /= 2;
			while (precision > 0.5) {
				const beforeLength: number = bestLength - precision;
				const before: IPosition = pathNode.getPointAtLength(beforeLength);
				const beforeDistance: number = distance2(before, point);
				const afterLength = bestLength + precision;
				const after: IPosition = pathNode.getPointAtLength(afterLength);
				const afterDistance: number = distance2(after, point);
				if (beforeLength >= 0 && beforeDistance < bestDistance) {
					best = before;
					bestLength = beforeLength;
					bestDistance = beforeDistance;
				} else if (afterLength <= pathLength && afterDistance < bestDistance) {
					best = after;
					bestLength = afterLength;
					bestDistance = afterDistance;
				} else {
					precision /= 2;
				}
			}

			const result = {
				best,
				distance: 0,
				length: 0,
				prevIndex: bestPi,
			};
			const useLength = bestLength - bestPrevLength;
			const totalLength = (bestPi == shapePoints.length - 2) ? pathLength - bestPrevLength : bestNextLength - bestPrevLength;
			result.length = useLength / totalLength;
			result.distance = Math.sqrt(bestDistance);
			return result;
		}
		function distance2(p: IPosition, point: number[]) {
			const dx = p.x - point[0];
			const dy = p.y - point[1];
			return dx * dx + dy * dy;
		}
	}
}

export default CurveTransientPoint;
